import { useEffect, useState } from 'preact/hooks';
import type { Api } from '../api.js';
import { texts, useLang, type Lang } from '../i18n.js';
import { Avatar, Callout, ConfirmDialog, Dialog, MoreOptions, Pill, toast } from '../ui.js';
import type { AgreementRow } from './Agreements.js';

/**
 * E-signing on a quote (owner 2026-10-27): "Send for signing" chooses who is on it and how, and the panel follows it –
 * who has opened, approved and signed, the comments with KFF's answers, reminders, the log and the PDFs.
 */

type Role = 'signer' | 'reviewer' | 'copy';
type Side = 'client' | 'kff';
type PartyStatus = 'waiting' | 'pending' | 'done' | 'declined';
interface Party {
  id: string;
  position: number;
  side: Side;
  company: string;
  role: Role;
  name: string;
  email: string;
  title: string | null;
  status: PartyStatus;
  viewedAt: string | null;
  verifiedAt: string | null;
  doneAt: string | null;
  declineReason: string | null;
}
export interface SigningState {
  canSend: boolean;
  options: { contacts: { name: string; email: string; title: string | null }[]; kff: { id: string; name: string; email: string }[]; me: string };
  history: { id: string; status: string; sentAt: string }[];
  request: null | {
    id: string;
    status: 'open' | 'completed' | 'declined' | 'revoked' | 'expired';
    ordered: boolean;
    message: string | null;
    sentAt: string;
    sentBy: string | null;
    expiresAt: string;
    completedAt: string | null;
    parties: Party[];
    events: { id: string; at: string; kind: string; who: string; text: string; ip: string | null }[];
    comments: { id: string; at: string; who: string; side: Side; text: string }[];
  };
  links?: { partyId: string; name: string; email: string; url: string }[];
}

const TX = texts(
  {
    title: 'Signing',
    send: 'Send for signing',
    sendAgain: 'Send for signing again',
    st: { open: 'Out for signing', completed: 'Signed by everyone', declined: 'Declined', revoked: 'Revoked', expired: 'Expired' } as Record<string, string>,
    sentBy: (who: string, at: string) => `Sent by ${who} ${at}`,
    expires: (at: string) => `The links work until ${at}`,
    ps: { waiting: 'Waiting', pending: 'Their turn', done: 'Done', declined: 'Declined' } as Record<string, string>,
    roles: { signer: 'Signs', reviewer: 'Reviews', copy: 'Gets a copy' } as Record<string, string>,
    opened: 'opened',
    remind: 'Remind',
    copyLink: 'Copy link',
    copied: 'Link copied',
    reminded: (n: string) => `Reminder sent to ${n}`,
    notMailed: 'The Hub could not email these people (on staging only @klarafardigafilm.se gets mail). Copy their link:',
    comments: 'Comments',
    noComments: 'No comments yet. The client can ask questions on the signing page and you answer here.',
    answer: 'Your answer',
    reply: 'Answer',
    log: (n: number) => `Log (${n})`,
    revoke: 'Revoke',
    revokeQ: 'Revoke the signing?',
    revokeBody: 'Every link stops working. The quote stays as it is, and you can send it again.',
    openPdf: 'Open the PDF',
    openSigned: 'Open the signed PDF',
    // the dialog
    dlgTitle: 'Send for signing',
    who: 'Who is on it',
    side: { client: 'Client', kff: 'KFF' } as Record<string, string>,
    sideLbl: 'Side',
    role: 'Role',
    name: 'Name',
    person: 'Person at KFF',
    email: 'Email',
    titleLbl: 'Title',
    remove: (n: string) => `Remove ${n || 'the row'}`,
    up: 'Move up',
    down: 'Move down',
    addClient: 'Add from the client',
    addKff: 'Add from KFF',
    ordered: 'Signing order: one at a time, top to bottom',
    orderedHint: 'Otherwise everyone gets it at once. A copy gets the signed PDF at the end.',
    kffNow: 'I sign for KFF as I send',
    kffNowHint: 'Add yourself as KFF’s signer. Otherwise KFF’s signer gets a link like the client.',
    reminders: 'Remind automatically after 3 days',
    message: 'Message to everyone (optional)',
    sendBtn: 'Send for signing',
    sending: 'Making the PDF and sending…',
    cancel: 'Cancel',
    sent: (n: number) => `Sent for signing to ${n}`,
    needSigner: 'Add who signs for the client.',
    choose: 'Choose…',
    // a model release (the Actors tab)
    releaseTitle: (n: string) => `Model release · ${n}`,
    releaseRevokeBody: 'The link stops working. The release is Not sent again, and you can send it again.',
    close: 'Close'
  },
  {
    title: 'Signering',
    send: 'Skicka för signering',
    sendAgain: 'Skicka för signering igen',
    st: { open: 'Ute för signering', completed: 'Signerat av alla', declined: 'Avböjt', revoked: 'Återkallat', expired: 'Utgånget' } as Record<string, string>,
    sentBy: (who: string, at: string) => `Skickat av ${who} ${at}`,
    expires: (at: string) => `Länkarna gäller till ${at}`,
    ps: { waiting: 'Väntar', pending: 'Står på tur', done: 'Klar', declined: 'Avböjde' } as Record<string, string>,
    roles: { signer: 'Signerar', reviewer: 'Granskar', copy: 'Får kopia' } as Record<string, string>,
    opened: 'har öppnat',
    remind: 'Påminn',
    copyLink: 'Kopiera länk',
    copied: 'Länken är kopierad',
    reminded: (n: string) => `Påminnelse skickad till ${n}`,
    notMailed: 'Hubben kunde inte mejla de här personerna (på staging får bara @klarafardigafilm.se mejl). Kopiera deras länk:',
    comments: 'Kommentarer',
    noComments: 'Inga kommentarer än. Kunden kan ställa frågor på signeringssidan och du svarar här.',
    answer: 'Ditt svar',
    reply: 'Svara',
    log: (n: number) => `Logg (${n})`,
    revoke: 'Återkalla',
    revokeQ: 'Återkalla signeringen?',
    revokeBody: 'Alla länkar slutar fungera. Offerten är kvar som den är och du kan skicka den igen.',
    openPdf: 'Öppna PDF:en',
    openSigned: 'Öppna signerad PDF',
    dlgTitle: 'Skicka för signering',
    who: 'Vilka som är med',
    side: { client: 'Kund', kff: 'KFF' } as Record<string, string>,
    sideLbl: 'Sida',
    role: 'Roll',
    name: 'Namn',
    person: 'Person på KFF',
    email: 'E-post',
    titleLbl: 'Titel',
    remove: (n: string) => `Ta bort ${n || 'raden'}`,
    up: 'Flytta upp',
    down: 'Flytta ner',
    addClient: 'Lägg till från kunden',
    addKff: 'Lägg till från KFF',
    ordered: 'Signeringsordning: en i taget, uppifrån och ner',
    orderedHint: 'Annars får alla det samtidigt. En kopia får den signerade PDF:en på slutet.',
    kffNow: 'Jag signerar för KFF när jag skickar',
    kffNowHint: 'Lägg till dig själv som KFF:s undertecknare. Annars får KFF:s undertecknare en länk precis som kunden.',
    reminders: 'Påminn automatiskt efter 3 dagar',
    message: 'Meddelande till alla (valfritt)',
    sendBtn: 'Skicka för signering',
    sending: 'Skapar PDF:en och skickar…',
    cancel: 'Avbryt',
    sent: (n: number) => `Skickat för signering till ${n}`,
    needSigner: 'Lägg till vem som signerar för kunden.',
    choose: 'Välj…',
    releaseTitle: (n: string) => `Modellavtal · ${n}`,
    releaseRevokeBody: 'Länken slutar fungera. Modellavtalet blir Inte skickat igen och du kan skicka det på nytt.',
    close: 'Stäng'
  }
);

const at = (iso: string, lang: Lang) => new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' }).format(new Date(iso));
const day = (iso: string, lang: Lang) => new Intl.DateTimeFormat(lang === 'sv' ? 'sv-SE' : 'en-GB', { timeZone: 'Europe/Stockholm', day: 'numeric', month: 'short' }).format(new Date(iso));
const errText = (e: unknown) => (e instanceof Error ? e.message : String(e));
const tone = (s: string) => (s === 'completed' || s === 'done' ? 'ok' : s === 'declined' ? 'crit' : s === 'open' || s === 'pending' ? 'info' : 'neutral');

export async function copy(url: string, lang: Lang) {
  try {
    await navigator.clipboard.writeText(url);
    toast({ text: TX[lang].copied });
  } catch {
    prompt(TX[lang].copyLink, url);
  }
}

/** The quote's signing: a button to send, then the state of the latest round. */
export function SigningPanel(p: { api: Api; a: AgreementRow; onChange: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [s, setS] = useState<SigningState | null>(null);
  const [sending, setSending] = useState(false);
  const [revoking, setRevoking] = useState(false);
  const [links, setLinks] = useState<SigningState['links']>([]);
  const [err, setErr] = useState('');
  const base = `/api/agreements/${p.a.id}/signing`;
  const load = () => p.api.get<SigningState>(base).then(setS, () => setS(null));
  useEffect(() => {
    void load();
  }, [p.a.id, p.a.rev]);
  if (!s) return null;
  const r = s.request;
  const open = r?.status === 'open';
  const canStart = s.canSend && (p.a.status === 'draft' || p.a.status === 'sent') && !open;
  const run = async (f: () => Promise<unknown>) => {
    setErr('');
    try {
      await f();
    } catch (e) {
      setErr(errText(e));
    }
  };
  const pdf = (which: 'original' | 'final') =>
    run(async () => {
      const f = await p.api.get<{ name: string; mimeType: string; data: string }>(`${base}/pdf?which=${which}`);
      const bytes = Uint8Array.from(atob(f.data), (ch) => ch.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: f.mimeType })), '_blank', 'noopener');
    });
  if (!r && !canStart) return null;
  return (
    <section class="card stack signing-panel" aria-labelledby="signing-h">
      <div class="cluster" style="justify-content:space-between">
        <h2 id="signing-h" class="section-title">
          {t.title} {r ? <Pill tone={tone(r.status)}>{t.st[r.status]}</Pill> : null}
        </h2>
        <span class="cluster">
          {r ? (
            <button type="button" class="btn btn-sm" onClick={() => void pdf(r.status === 'completed' ? 'final' : 'original')}>
              {r.status === 'completed' ? t.openSigned : t.openPdf}
            </button>
          ) : null}
          {open && s.canSend ? (
            <button type="button" class="btn btn-sm btn-ghost" onClick={() => setRevoking(true)}>
              {t.revoke}
            </button>
          ) : null}
          {canStart ? (
            <button type="button" class="btn btn-primary" onClick={() => setSending(true)}>
              {r ? t.sendAgain : t.send}
            </button>
          ) : null}
        </span>
      </div>
      {r ? <SigningRound api={p.api} base={base} r={r} canSend={s.canSend} links={links ?? []} onState={setS} /> : null}
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
      {sending ? (
        <SendDialog
          api={p.api}
          a={p.a}
          s={s}
          onClose={() => setSending(false)}
          onDone={(x) => {
            setSending(false);
            setS(x);
            setLinks(x.links ?? []);
            toast({ text: t.sent(x.request?.parties.filter((y) => y.role !== 'copy').length ?? 0) });
            p.onChange();
          }}
        />
      ) : null}
      {revoking ? (
        <ConfirmDialog
          title={t.revokeQ}
          body={t.revokeBody}
          confirmLabel={t.revoke}
          danger
          onClose={() => setRevoking(false)}
          onConfirm={async () => {
            setS(await p.api.post<SigningState>(`${base}/revoke`));
            setRevoking(false);
            setLinks([]);
          }}
        />
      ) : null}
    </section>
  );
}

/** One round of signing: who is on it and where they are, the links to copy, the comments with KFF's answers, the log. */
export function SigningRound(p: { api: Api; base: string; r: NonNullable<SigningState['request']>; canSend: boolean; links: NonNullable<SigningState['links']>; onState: (s: SigningState) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const { r, base, links } = p;
  const s = { canSend: p.canSend };
  const setS = p.onState;
  const open = r.status === 'open';
  const [reply, setReply] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const run = async (f: () => Promise<unknown>) => {
    setBusy(true);
    setErr('');
    try {
      await f();
    } catch (e) {
      setErr(errText(e));
    } finally {
      setBusy(false);
    }
  };
  return (
    <>
    <p class="small muted">
      {t.sentBy(r.sentBy ?? 'KFF', at(r.sentAt, lang))}
      {open ? ` · ${t.expires(day(r.expiresAt, lang))}` : ''}
    </p>
    {links?.length ? (
      <Callout tone="info" title={t.notMailed}>
        <ul>
          {links.map((l) => (
            <li key={l.partyId}>
              {l.name} ({l.email}){' '}
              <button type="button" class="btn btn-sm" onClick={() => void copy(l.url, lang)}>
                {t.copyLink}
              </button>
            </li>
          ))}
        </ul>
      </Callout>
    ) : null}
    <ol class="signing-parties">
      {r.parties.map((x) => (
        <li key={x.id}>
          <span class="pos">{r.ordered && x.role !== 'copy' ? `${x.position}.` : ''}</span>
          <Avatar name={x.name} size="sm" client={x.side === 'client'} label={`${x.name} · ${x.company}`} />
          <span>
            {x.name}
            <span class="small muted">
              {' '}
              · {t.roles[x.role]} · {x.company}
              {x.viewedAt && x.status === 'pending' ? ` · ${t.opened} ${at(x.viewedAt, lang)}` : ''}
              {x.doneAt && x.status === 'done' ? ` · ${at(x.doneAt, lang)}` : ''}
              {x.declineReason ? ` · ”${x.declineReason}”` : ''}
            </span>
          </span>
          <span class="cluster">
            {x.role !== 'copy' ? <Pill tone={tone(x.status)}>{t.ps[x.status]}</Pill> : null}
            {open && x.status === 'pending' && s.canSend ? (
              <button
                type="button"
                class="btn btn-sm btn-ghost"
                disabled={busy}
                onClick={() =>
                  void run(async () => {
                    const res = await p.api.post<{ mailed: boolean; url: string; state: SigningState }>(`${base}/remind`, { partyId: x.id });
                    setS(res.state);
                    if (res.mailed) toast({ text: t.reminded(x.name) });
                    else await copy(res.url, lang);
                  })
                }
              >
                {t.remind}
              </button>
            ) : null}
          </span>
        </li>
      ))}
    </ol>
    <div class="stack">
      <h3 class="label">{t.comments}</h3>
      {r.comments.length ? (
        <ol class="esign-comments">
          {r.comments.map((c) => (
            <li key={c.id} class={`esign-comment esign-comment--${c.side}`}>
              <div class="small muted">
                {c.who} · {at(c.at, lang)}
              </div>
              <p>{c.text}</p>
            </li>
          ))}
        </ol>
      ) : (
        <p class="hint">{t.noComments}</p>
      )}
      {open ? (
        <form
          class="stack"
          onSubmit={(e) => {
            e.preventDefault();
            void run(async () => {
              setS(await p.api.post<SigningState>(`${base}/comment`, { text: reply }));
              setReply('');
            });
          }}
        >
          <label class="field">
            <span class="label sr-only">{t.answer}</span>
            <textarea class="input input--wide" rows={2} placeholder={t.answer} value={reply} onInput={(e) => setReply((e.target as HTMLTextAreaElement).value)} />
          </label>
          <div>
            <button type="submit" class="btn btn-sm" disabled={busy || !reply.trim()}>
              {t.reply}
            </button>
          </div>
        </form>
      ) : null}
    </div>
    <details class="more">
      <summary>{t.log(r.events.length)}</summary>
      <ol class="signing-log more-body">
        {r.events.map((e) => (
          <li key={e.id}>
            <time>{at(e.at, lang)}</time>
            {e.who} {e.text}
            {e.ip ? <span class="muted"> · IP {e.ip}</span> : null}
          </li>
        ))}
      </ol>
    </details>
      {err ? (
        <p class="field-error" role="alert">
          {err}
        </p>
      ) : null}
    </>
  );
}

interface Row {
  key: number;
  side: Side;
  role: Role;
  name: string;
  email: string;
  title: string;
  personId: string;
}

let rowKey = 0;
const row = (o: Partial<Row>): Row => ({ key: ++rowKey, side: 'client', role: 'signer', name: '', email: '', title: '', personId: '', ...o });

/** The first rows: the client's signer from the quote, then the sender as KFF's signer. */
export function startRows(a: AgreementRow, s: SigningState): Row[] {
  const signer = a.terms.party?.signerName ?? a.contactName ?? '';
  const match = s.options.contacts.find((c) => c.name === signer) ?? s.options.contacts[0];
  const first = row({ name: signer || match?.name || '', email: (signer && match?.name === signer ? match.email : a.contactName === signer ? (a.contactEmail ?? '') : '') || match?.email || '', title: a.terms.party?.signerTitle ?? '' });
  const me = s.options.kff.find((k) => k.id === s.options.me);
  return [first, ...(me ? [row({ side: 'kff', personId: me.id, name: me.name, email: me.email })] : [])];
}

export function SendDialog(p: { api: Api; a: AgreementRow; s: SigningState; onClose: () => void; onDone: (s: SigningState) => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [rows, setRows] = useState<Row[]>(() => startRows(p.a, p.s));
  const [ordered, setOrdered] = useState(false);
  const [kffNow, setKffNow] = useState(true);
  const [reminders, setReminders] = useState(true);
  const [message, setMessage] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState('');
  const me = p.s.options.me;
  const iSign = rows.some((r) => r.side === 'kff' && r.role === 'signer' && r.personId === me);
  const hasSigner = rows.some((r) => r.side === 'client' && r.role === 'signer' && r.name.trim() && r.email.trim());
  const set = (key: number, patch: Partial<Row>) => setRows((rs) => rs.map((r) => (r.key === key ? { ...r, ...patch } : r)));
  const move = (i: number, d: -1 | 1) =>
    setRows((rs) => {
      const j = i + d;
      if (j < 0 || j >= rs.length) return rs;
      const out = rs.slice();
      [out[i], out[j]] = [out[j]!, out[i]!];
      return out;
    });
  const send = async () => {
    setBusy(true);
    setErr('');
    try {
      const parties = rows
        .filter((r) => (r.side === 'kff' ? r.personId : r.name.trim() || r.email.trim()))
        .map((r) => (r.side === 'kff' ? { side: 'kff', role: r.role, personId: r.personId, title: r.title || null } : { side: 'client', role: r.role, name: r.name, email: r.email, title: r.title || null }));
      p.onDone(await p.api.post<SigningState>(`/api/agreements/${p.a.id}/signing`, { parties, ordered, kffSignsNow: kffNow && iSign, reminders, message: message || null }));
    } catch (e) {
      setErr(errText(e));
      setBusy(false);
    }
  };
  return (
    <Dialog
      title={t.dlgTitle}
      sub={`Offert ${p.a.ref} · ${p.a.name}`}
      onClose={p.onClose}
      foot={
        <>
          <button type="button" class="btn" onClick={p.onClose} disabled={busy}>
            {t.cancel}
          </button>
          <button type="button" class="btn btn-primary" disabled={busy || !hasSigner} onClick={() => void send()}>
            {busy ? t.sending : t.sendBtn}
          </button>
        </>
      }
    >
      <div class="stack">
        <h3 class="label">{t.who}</h3>
        <datalist id="sg-contacts">
          {p.s.options.contacts.map((c) => (
            <option key={c.email} value={c.name} />
          ))}
        </datalist>
        {rows.map((r, i) => (
          <div key={r.key} class="signing-row">
            <label class="field">
              <span class="label">{t.sideLbl}</span>
              <select class="input" value={r.side} onChange={(e) => set(r.key, { side: (e.target as HTMLSelectElement).value as Side, personId: '', name: '', email: '' })}>
                <option value="client">{t.side.client}</option>
                <option value="kff">{t.side.kff}</option>
              </select>
            </label>
            <label class="field">
              <span class="label">{t.role}</span>
              <select class="input" value={r.role} onChange={(e) => set(r.key, { role: (e.target as HTMLSelectElement).value as Role })}>
                <option value="signer">{t.roles.signer}</option>
                <option value="reviewer">{t.roles.reviewer}</option>
                <option value="copy">{t.roles.copy}</option>
              </select>
            </label>
            {r.side === 'kff' ? (
              <label class="field" style="grid-column: span 2">
                <span class="label">{t.person}</span>
                <select
                  class="input input--wide"
                  value={r.personId}
                  onChange={(e) => {
                    const k = p.s.options.kff.find((x) => x.id === (e.target as HTMLSelectElement).value);
                    set(r.key, { personId: k?.id ?? '', name: k?.name ?? '', email: k?.email ?? '' });
                  }}
                >
                  <option value="">{t.choose}</option>
                  {p.s.options.kff.map((k) => (
                    <option key={k.id} value={k.id}>
                      {k.name}
                    </option>
                  ))}
                </select>
              </label>
            ) : (
              <>
                <label class="field">
                  <span class="label">{t.name}</span>
                  <input
                    class="input input--wide"
                    list="sg-contacts"
                    value={r.name}
                    onInput={(e) => {
                      const name = (e.target as HTMLInputElement).value;
                      const c = p.s.options.contacts.find((x) => x.name === name);
                      set(r.key, { name, ...(c && !r.email ? { email: c.email } : {}) });
                    }}
                  />
                </label>
                <label class="field">
                  <span class="label">{t.email}</span>
                  <input class="input input--wide" type="email" value={r.email} onInput={(e) => set(r.key, { email: (e.target as HTMLInputElement).value })} />
                </label>
              </>
            )}
            <span class="cluster">
              {ordered ? (
                <>
                  <button type="button" class="btn btn-sm btn-ghost" aria-label={t.up} title={t.up} disabled={i === 0} onClick={() => move(i, -1)}>
                    ↑
                  </button>
                  <button type="button" class="btn btn-sm btn-ghost" aria-label={t.down} title={t.down} disabled={i === rows.length - 1} onClick={() => move(i, 1)}>
                    ↓
                  </button>
                </>
              ) : null}
              <button type="button" class="btn btn-sm btn-ghost" aria-label={t.remove(r.name)} title={t.remove(r.name)} onClick={() => setRows((rs) => rs.filter((x) => x.key !== r.key))}>
                ×
              </button>
            </span>
          </div>
        ))}
        <div class="cluster">
          <button type="button" class="btn btn-sm" onClick={() => setRows((rs) => [...rs, row({})])}>
            {t.addClient}
          </button>
          <button type="button" class="btn btn-sm" onClick={() => setRows((rs) => [...rs, row({ side: 'kff' })])}>
            {t.addKff}
          </button>
        </div>
        {!hasSigner ? <p class="hint">{t.needSigner}</p> : null}
        <label class="check">
          <input type="checkbox" checked={ordered} onChange={(e) => setOrdered((e.target as HTMLInputElement).checked)} />
          <span>
            {t.ordered}
            <span class="hint"> {t.orderedHint}</span>
          </span>
        </label>
        <label class="check">
          <input type="checkbox" checked={kffNow && iSign} disabled={!iSign} onChange={(e) => setKffNow((e.target as HTMLInputElement).checked)} />
          <span>
            {t.kffNow}
            {!iSign ? <span class="hint"> {t.kffNowHint}</span> : null}
          </span>
        </label>
        <MoreOptions id="signing-send" filled={(message ? 1 : 0) + (reminders ? 0 : 1)}>
          <label class="field">
            <span class="label">{t.message}</span>
            <textarea class="input input--wide" rows={3} value={message} maxLength={2000} onInput={(e) => setMessage((e.target as HTMLTextAreaElement).value)} />
          </label>
          <label class="check">
            <input type="checkbox" checked={reminders} onChange={(e) => setReminders((e.target as HTMLInputElement).checked)} />
            <span>{t.reminders}</span>
          </label>
        </MoreOptions>
        {err ? (
          <p class="field-error" role="alert">
            {err}
          </p>
        ) : null}
      </div>
    </Dialog>
  );
}

/** A model release's signing on the Actors tab: the same round as a quote's, with the PDF and Revoke. */
export function ReleaseSigningDialog(p: { api: Api; base: string; name: string; links?: SigningState['links']; onClose: () => void; onChange: () => void }) {
  const lang = useLang();
  const t = TX[lang];
  const [s, setS] = useState<SigningState | null>(null);
  const [revoking, setRevoking] = useState(false);
  const [err, setErr] = useState('');
  useEffect(() => {
    p.api.get<SigningState>(p.base).then(setS, (e) => setErr(errText(e)));
  }, [p.base]);
  const r = s?.request ?? null;
  const pdf = async (which: 'original' | 'final') => {
    try {
      const f = await p.api.get<{ name: string; mimeType: string; data: string }>(`${p.base}/pdf?which=${which}`);
      const bytes = Uint8Array.from(atob(f.data), (ch) => ch.charCodeAt(0));
      window.open(URL.createObjectURL(new Blob([bytes], { type: f.mimeType })), '_blank', 'noopener');
    } catch (e) {
      setErr(errText(e));
    }
  };
  return (
    <Dialog
      title={t.releaseTitle(p.name)}
      sub={r ? <Pill tone={tone(r.status)}>{t.st[r.status]}</Pill> : null}
      onClose={p.onClose}
      foot={
        <>
          {r?.status === 'open' ? (
            <button type="button" class="btn btn-ghost" onClick={() => setRevoking(true)}>
              {t.revoke}
            </button>
          ) : null}
          {r ? (
            <button type="button" class="btn" onClick={() => void pdf(r.status === 'completed' ? 'final' : 'original')}>
              {r.status === 'completed' ? t.openSigned : t.openPdf}
            </button>
          ) : null}
          <button type="button" class="btn btn-primary" onClick={p.onClose}>
            {t.close}
          </button>
        </>
      }
    >
      <div class="stack signing-panel">
        {r && s ? (
          <SigningRound
            api={p.api}
            base={p.base}
            r={r}
            canSend={s.canSend}
            links={p.links ?? []}
            onState={(x) => {
              setS(x);
              p.onChange();
            }}
          />
        ) : null}
        {err ? (
          <p class="field-error" role="alert">
            {err}
          </p>
        ) : null}
      </div>
      {revoking ? (
        <ConfirmDialog
          title={t.revokeQ}
          body={t.releaseRevokeBody}
          confirmLabel={t.revoke}
          danger
          onClose={() => setRevoking(false)}
          onConfirm={async () => {
            setS(await p.api.post<SigningState>(`${p.base}/revoke`));
            setRevoking(false);
            p.onChange();
          }}
        />
      ) : null}
    </Dialog>
  );
}
